// Silence-trim analysis for playback.
//
// Given a verse audio file URL, decode it with the Web Audio API and find the
// bounds of actual speech (leading + trailing silence removed), keeping a small
// natural pad on each side. Results are cached per-URL so each file is only
// fetched/decoded once, and analysis can be pre-warmed for upcoming verses.

const cache = new Map(); // url -> { start, end, duration } | null (analysis failed -> play full)
const inflight = new Map(); // url -> Promise

// Tuning
const LEAD_PAD = 0.15; // seconds of room kept before the detected onset
const TAIL_PAD = 0.2; // seconds of room kept after the detected offset
const WINDOW_SEC = 0.02; // 20ms RMS windows
const REL_THRESHOLD = 0.06; // a window counts as clear speech at >=6% of the file's peak
const ONSET_REL_FLOOR = 0.018; // much lower bar used to recover soft consonant onsets ("Bl-", "S-")
const ABS_FLOOR = 0.004; // absolute noise floor for the speech threshold
const ONSET_GAP_TOLERANCE = 1; // allow this many sub-floor windows while tracing an onset/offset

/**
 * Analyze a single audio file for leading/trailing silence.
 * @returns {Promise<{start:number,end:number,duration:number}|null>}
 *   `start`/`end` are seconds to play between. `null` means "couldn't analyze,
 *   play the whole file" (graceful fallback).
 */
export async function analyzeTrim(url) {
  if (!url) return null;
  if (cache.has(url)) return cache.get(url);
  if (inflight.has(url)) return inflight.get(url);

  const p = (async () => {
    let ctx;
    try {
      const res = await fetch(url);
      const contentType = res.headers.get('content-type') || '';
      const arrayBuffer = await res.arrayBuffer();

      // Real duration per the media element (reliable for every container).
      // decodeAudioData silently DROPS the later fragments of fragmented MP4
      // (iOS/Chrome MediaRecorder output), returning a buffer much shorter
      // than the real audio. Trusting that truncated buffer would set the
      // trim end mid-verse and cut playback off in the middle — so if the
      // decoded duration falls short of the real duration, skip trimming.
      const realDuration = await new Promise((resolve) => {
        const blobUrl = URL.createObjectURL(new Blob([arrayBuffer.slice(0)], contentType ? { type: contentType } : undefined));
        const el = new Audio();
        let settled = false;
        const finish = (d) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          URL.revokeObjectURL(blobUrl);
          resolve(d);
        };
        const timer = setTimeout(() => finish(null), 3000);
        el.onloadedmetadata = () => finish(isFinite(el.duration) ? el.duration : null);
        el.onerror = () => finish(null);
        el.src = blobUrl;
      });

      const Ctx = window.AudioContext || window.webkitAudioContext;
      ctx = new Ctx();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

      if (realDuration != null && audioBuffer.duration < realDuration - Math.max(0.5, realDuration * 0.1)) {
        console.warn(
          `trimSilence: decoder returned ${audioBuffer.duration.toFixed(2)}s of a ${realDuration.toFixed(2)}s file (fragmented container?) — skipping trim for`,
          url
        );
        cache.set(url, null);
        return null;
      }

      const data = audioBuffer.getChannelData(0);
      const sr = audioBuffer.sampleRate;
      const duration = audioBuffer.duration;
      const win = Math.max(1, Math.floor(WINDOW_SEC * sr));

      // Peak amplitude across the whole file -> relative threshold.
      let peak = 0;
      for (let i = 0; i < data.length; i++) {
        const a = data[i] < 0 ? -data[i] : data[i];
        if (a > peak) peak = a;
      }
      const speechThreshold = Math.max(ABS_FLOOR, peak * REL_THRESHOLD);
      // A much lower bar, used only to trace back/forward into the soft consonant
      // onset/offset of a word (e.g. the "Bl" in "Blessed"), which is far quieter
      // than the vowel and would otherwise be clipped.
      const onsetFloor = Math.max(ABS_FLOOR * 0.6, peak * ONSET_REL_FLOOR);

      const windowRms = (start) => {
        const end = Math.min(data.length, start + win);
        let sum = 0;
        for (let i = start; i < end; i++) sum += data[i] * data[i];
        return Math.sqrt(sum / Math.max(1, end - start));
      };

      // First window (from the start) that clearly contains speech.
      let firstSound = -1;
      for (let s = 0; s < data.length; s += win) {
        if (windowRms(s) >= speechThreshold) {
          firstSound = s;
          break;
        }
      }
      // Last window (from the end) that clearly contains speech.
      let lastSound = -1;
      for (let s = Math.max(0, data.length - win); s >= 0; s -= win) {
        if (windowRms(s) >= speechThreshold) {
          lastSound = Math.min(data.length, s + win);
          break;
        }
      }

      let result;
      if (firstSound < 0 || lastSound < 0 || lastSound <= firstSound) {
        // Effectively silent / undetectable -> don't trim.
        result = { start: 0, end: duration, duration };
      } else {
        // Trace backward into the quiet consonant onset that precedes the vowel.
        let gaps = 0;
        for (let s = firstSound - win; s >= 0; s -= win) {
          if (windowRms(s) >= onsetFloor) {
            firstSound = s;
            gaps = 0;
          } else if (++gaps > ONSET_GAP_TOLERANCE) {
            break;
          }
        }
        // Trace forward into the quiet consonant tail after the last vowel.
        gaps = 0;
        for (let s = lastSound; s + win <= data.length; s += win) {
          if (windowRms(s) >= onsetFloor) {
            lastSound = Math.min(data.length, s + win);
            gaps = 0;
          } else if (++gaps > ONSET_GAP_TOLERANCE) {
            break;
          }
        }

        const start = Math.max(0, firstSound / sr - LEAD_PAD);
        const end = Math.min(duration, lastSound / sr + TAIL_PAD);
        // Safety: never collapse to nothing.
        result = end - start < 0.15 ? { start: 0, end: duration, duration } : { start, end, duration };
      }

      cache.set(url, result);
      return result;
    } catch (e) {
      console.warn('trimSilence: analysis failed, will play full file:', url, e);
      cache.set(url, null);
      return null;
    } finally {
      inflight.delete(url);
      try {
        ctx?.close?.();
      } catch (_) {}
    }
  })();

  inflight.set(url, p);
  return p;
}

/**
 * Synchronously read a previously-computed trim result.
 * @returns the cached value, or `undefined` if not analyzed yet.
 */
export function getCachedTrim(url) {
  return cache.has(url) ? cache.get(url) : undefined;
}

/** Fire-and-forget pre-warm so the result is ready before playback reaches it. */
export function prewarmTrim(url) {
  if (url && !cache.has(url) && !inflight.has(url)) analyzeTrim(url);
}
